import { useState, useMemo, useEffect, useCallback } from 'react';
import IconImages from '@shared/assets/images/icons';
import { Input } from '@shared/components/common/Input';
import Pagination from '@shared/components/pagination/Pagination';
import Table from '@shared/components/table/Table';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useStore } from '@shared/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_YMD } from '@shared/components/constants/common.constants';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '@shared/configs/constants';
import Tooltip from '@shared/components/common/Tooltip';

const UserSubscriptionListPage = observer(() => {
    const { groupAdminStore } = useStore();
    const navigate = useNavigate();
    const { userSubscriptions, totalUserSubscriptions, loading } = groupAdminStore;

    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const fetchSubscriptions = (page: number) => {
        groupAdminStore.fetchUserSubscriptions({
            page,
            size: pageSize,
            search: searchText.trim()
        });
    };

    useEffect(() => {
        fetchSubscriptions(currentPage);
    }, [currentPage]);

    const handleSearch = () => {
        if (currentPage === 1) {
            fetchSubscriptions(1);
        } else {
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const columns: ColumnDef<any>[] = useMemo(() => [
        {
            accessorKey: 'no',
            header: 'No.',
            cell: ({ row }) => (currentPage - 1) * pageSize + row.index + 1,
            size: 60,
            enableSorting: false
        },
        {
            accessorKey: 'userId',
            header: 'ユーザーID',
            cell: ({ row }) => (
                <span
                    className='text-[#0095D9] cursor-pointer hover:underline'
                    onClick={() => navigate(`${ADMIN_ROUTES.userManagement.href}/${row.original.id}`)}

                >
                    {row.original.userId || '--'}
                </span>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'customerCode',
            header: 'お客様番号',
            cell: ({ row }) => row.original.customerCode || '--',
            enableSorting: false
        },
        {
            accessorKey: 'userName',
            header: '氏名',
            size: 240,
            cell: ({ row }) => (
                <div className='flex flex-col items-center gap-[10px]'>
                    <Tooltip content={row.original.userNameFuri || '--'} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                        <div className='line-clamp-2'>{row.original.userNameFuri || '--'}</div>
                    </Tooltip>
                    <Tooltip content={row.original.userName || '--'} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                        <div className='text-[#6F767E] line-clamp-2'>{row.original.userName || '--'}</div>
                    </Tooltip>
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'pharmacistRegNumber',
            header: '薬剤師名簿登録番号',
            cell: ({ row }) => row.original.pharmacistRegNumber || '--',
            enableSorting: false
        },
        {
            accessorKey: 'email',
            header: 'メールアドレス',
            size: 280,
            cell: ({ row }) => (
                <Tooltip content={row.original.email || '--'} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                    <div className='line-clamp-2'>{row.original.email || '--'}</div>
                </Tooltip>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'productCount',
            header: '商品数',
            cell: ({ row }) => (
                <div className='flex items-center justify-center gap-2'>
                    <button
                        onClick={row.getToggleExpandedHandler()}
                        className='p-1 hover:bg-gray-100 rounded transition-colors'
                    >
                        {row.getIsExpanded() ?
                            (
                                <img src={IconImages.minusSquareIc} alt='minus' />
                            ) :
                            (
                                <img src={IconImages.plusSquareIc} alt='plus' />
                            )}
                    </button>
                    <span>{row.original.productCount}</span>
                </div>
            ),
            enableSorting: false
        }
    ], [currentPage]);

    const renderSubComponent = useCallback(({ row }: { row: Row<any> }) => {
        const subscription = row.original;
        if (!subscription.subscriptionItems || subscription.subscriptionItems.length === 0) {
            return null;
        }

        return (
            <div className='-m-4 bg-[#F1F5F9] overflow-x-auto'>
                <table className='w-full min-w-[1024px] bg-[#F1F5F9] font-medium leading-[21px]'>
                    <thead className='border-b border-[#E5E6E6]'>
                        <tr className='text-[#6F767E] text-center '>
                            <th className='px-3 py-4 w-[67.11px]'>No.</th>
                            <th className='px-3 py-4 w-[240px]'>商品コード</th>
                            <th className='px-3 py-4 w-[400px]'>商品名</th>
                            <th className='px-3 py-4'>開始日</th>
                            <th className='px-3 py-4'>終了日</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subscription.subscriptionItems.map((item: any, itemIndex: number) => (
                            <tr key={item.id} className='text-center border-b border-[#E5E6E6] last:border-0'>
                                <td className='px-3 py-4'>{itemIndex + 1}</td>
                                <td className='px-3 py-4'>
                                    <Tooltip content={item.productCode} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                                        <div className='line-clamp-2'>{item.productCode}</div>
                                    </Tooltip>
                                </td>
                                <td className='px-3 py-4'>
                                    <Tooltip content={item.productName} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                                        <div className='line-clamp-2'>{item.productName}</div>
                                    </Tooltip>
                                </td>
                                <td className='px-3 py-4'>{formatDateTime(item.startDate, DATE_YMD, { returnDefault: '未定' })}</td>
                                <td className='px-3 py-4'>{formatDateTime(item.endDate, DATE_YMD, { returnDefault: '未定' })}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }, []);

    return (
        <section className='py-6'>
            {/* Page Title */}
            <h1 className='text-[30px] leading-[36px] font-semibold mb-10'>
                ユーザーの定期購読雑誌一覧
            </h1>

            {/* Search Card */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6 mb-6'>
                <div className='flex flex-col md:flex-row gap-4 items-start md:items-end'>
                    <div className='w-full md:w-1/2'>
                        <label className='block leading-[21px] mb-2'>
                            ユーザーID / 氏名
                        </label>
                        <div className='w-full'>
                            <Input
                                type='text'
                                placeholder=''
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                className='px-4 leading-[21px] rounded-[0px] h-[56px] border border-[#E7E7E7] w-full'
                            />
                        </div>
                    </div>
                    <button
                        onClick={handleSearch}
                        className='bg-primary rounded-[10px] text-white w-full md:w-[291px] h-[56px] font-semibold transition-colors'
                    >
                        検索
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6'>
                <Table
                    columns={columns}
                    data={userSubscriptions}
                    renderSubComponent={renderSubComponent}
                    getRowCanExpand={() => true}
                    showTableFooter={false}
                    showSelectPageSize={false}
                    loadingData={loading}
                    noData={<p className='text-[#999999]'>該当するデータが見つかりませんでした。</p>}
                    headerClassName='bg-[#F2F2F2] text-[#6F767E] font-medium'
                />

                {totalUserSubscriptions > 0 && (
                    <div className='flex justify-center py-4 border-t border-gray-200 mt-4'>
                        <Pagination
                            type='table-pagination'
                            paginationClassName='w-fit'
                            currentPage={currentPage}
                            totalRecords={totalUserSubscriptions}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </section>
    );
});

export default UserSubscriptionListPage;
